<script lang="ts" setup>
import { isMobile } from "@/modules/common";
import { useChatSession } from "@/stores/chatSession";
import { Popup, showLoadingToast, Tabs, Tab } from "vant";
import { computed, ref, watch } from "vue";
import { useAllGender, useShortCount } from "@/use/common";
import { useGlobalUser } from "@/stores/globalUser";
import { GenderEnum } from "@/enums/mateEnums";
import { useMate } from "@/stores/mate";
import { createPrivateSession } from "@/components/mobile/chat/chatUtils";
import { useI18n } from "vue-i18n";
import {
  getDefaultPersonaId,
  replacePlaceHolder,
} from "@/components/mobile/chat/mode/util";
import escapeHtml from "@/modules/escape-html";
import { marked } from "marked";
import { useChatScene } from "@/stores/chatScene";
import { useMatePersona } from "@/stores/matePersona";
import PersonaCard from "@/components/mobile/chat/persona/PersonaCard.vue";

// Stores
const sessionStore = useChatSession();
const user = useGlobalUser();
const mateStore = useMate();
const { t } = useI18n();
const chatSceneStore = useChatScene();
const personaStore = useMatePersona();
const chatSessionStore = useChatSession();
// States
const GenderOptions = useAllGender();
const targetMate = computed(() => sessionStore.chatPrepare.mate);
const gender = ref<GenderEnum>(GenderOptions[0].value);
const name = ref("");
const isSceneLoading = ref(false);
const isPersonaLoading = ref(false);
const sceneList = computed(() =>
  targetMate.value ? chatSceneStore.getChatSceneList(targetMate.value!.id!) : []
);
const selectedSceneId = ref<number | undefined>();
const selectedGenericSceneId = ref<number>();
const selectedPersonaId = ref<number | undefined>(undefined);

const chatPrepareTab = ref(0);

function parsePrompt(txt: string) {
  if (!txt) return "";
  const msg = replacePlaceHolder(
    txt,
    targetMate.value?.name ?? "",
    mateStore.currentMate?.name ?? ""
  );
  const content = escapeHtml(msg);
  return marked.parse(content);
}

const greetingMsg = computed(() => {
  if (!targetMate.value) return "";
  return parsePrompt(targetMate.value.greeting);
});
const roleSetting = ref<"default" | "custom">(
  mateStore.currentMate ? "default" : "custom"
);
const sceneTab = ref<"specific" | "generic">("specific");
const isConfirmDisabled = computed(() => {
  if (roleSetting.value === "custom") {
    return !name.value;
  }
  return false;
});
// const validMateList = computed(() => {
//   return mateStore.list.filter((item) => item.uuid !== targetMate.value?.uuid);
// });

// Lifecycle
watch(
  () => sessionStore.chatPrepare.showRoleSetting,
  async () => {
    if (!sessionStore.chatPrepare.showRoleSetting) return;
    roleSetting.value = mateStore.currentMate ? "default" : "custom";
    selectedSceneId.value =
      chatSessionStore.chatPrepare.sceneId ??
      chatSessionStore.chatPrepare.mate?.defaultChatSceneId;
    selectedGenericSceneId.value = undefined;
    selectedPersonaId.value = getDefaultPersonaId(targetMate.value);
    chatPrepareTab.value = 0;
    try {
      isSceneLoading.value = true;
      isPersonaLoading.value = true;
      const promiseList = [];
      if (targetMate.value) {
        promiseList.push(
          chatSceneStore.getOrFetchChatSceneList(targetMate.value.id!)
        );
      }
      promiseList.push(chatSceneStore.getOrFetchGenericChatSceneList());
      promiseList.push(
        personaStore
          .getOrFetchMatePersonaList()
          .finally(() => (isPersonaLoading.value = false))
      );
      await Promise.all(promiseList);
      if (chatSceneStore.genericChatSceneList.length > 0) {
        selectedGenericSceneId.value =
          chatSceneStore.genericChatSceneList[0].id;
      }
    } finally {
      isSceneLoading.value = false;
    }
  }
);

// Event Handler
const onConfirm = async () => {
  const loading = showLoadingToast(t("Message.preparingToCreateCharacter"));
  try {
    if (!user.isLogin) {
      // 1. 创建匿名账户
      if (!user.id) {
        await user.anonymousUserInit({ gender: gender.value as GenderEnum });
      }
      // 2. 更新角色名称
      await mateStore.updateMateDetail(mateStore.currentMate!.uuid, {
        name: name.value,
      });
      loading.close();
    }
    // 3. 创建session
    await createPrivateSession(
      targetMate.value!.id!,
      targetMate.value!.uuid,
      "push",
      sceneTab.value === "specific"
        ? selectedSceneId.value
        : selectedGenericSceneId.value,
      selectedPersonaId.value
    );
    sessionStore.chatPrepare.showRoleSetting = false;
  } finally {
    loading.close();
  }
};
</script>
<template>
  <Popup
    v-model:show="sessionStore.chatPrepare.showRoleSetting"
    :close-on-click-overlay="false"
    :position="isMobile() ? 'bottom' : 'center'"
    class="w-full lg:max-w-xl bg-base-100 overflow-hidden grid grid-rows-[auto_1fr] h-full lg:h-auto max-h-[80%]"
    round
  >
    <div class="flex justify-between items-center p-4">
      <div class="text-base font-bold">{{ targetMate?.name }}</div>
      <div class="flex gap-1">
        <button
          class="btn btn-sm"
          @click="sessionStore.chatPrepare.showRoleSetting = false"
        >
          {{ $t("Buttons.cancel") }}
        </button>
        <button
          :disabled="isConfirmDisabled"
          class="btn btn-sm btn-primary"
          @click="onConfirm"
        >
          {{ $t("Buttons.chat") }}
        </button>
      </div>
    </div>
    <div class="gap-3 p-4 pt-0 w-full h-full overflow-y-auto flex flex-col">
      <div class="rounded flex gap-2 max-h-32">
        <div class="flex flex-col gap-1 flex-grow">
          <div class="font-bold text-base line-clamp-1 shrink-0">
            {{ $t("User.bio") }}
          </div>
          <div class="flex-grow overflow-y-auto text-xs border p-2 rounded">
            <div>
              {{
                targetMate?.bio
                  ? targetMate?.bio
                  : $t("Texts.noIntroductionText")
              }}
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-1">
        <div class="flex justify-between items-center">
          <div class="font-bold text-base">
            {{ $t("loginmodal.setYourRole") }}
          </div>
          <div v-if="false" class="join">
            <button
              :class="{
                'btn-primary': roleSetting === 'default',
              }"
              class="join-item btn btn-xs"
              @click="roleSetting = 'default'"
            >
              {{ $t("Message.default") }}
            </button>
            <button
              :class="{
                'btn-primary': roleSetting === 'custom',
              }"
              class="join-item btn btn-xs"
              @click="roleSetting = 'custom'"
            >
              {{ $t("Message.custom") }}
            </button>
          </div>
        </div>
        <div v-if="roleSetting === 'custom'">
          <label class="form-control w-full">
            <div class="label">
              <span class="label-text">
                {{ $t("User.mate.basicInfo.name") }}
                <span class="text-red-500">*</span>
              </span>
            </div>
            <input
              v-model="name"
              :placeholder="$t('loginmodal.characterName')"
              class="input input-bordered w-full input-sm sm:input-md"
              type="text"
            />
          </label>
          <label class="form-control w-full">
            <div class="label">
              <span class="label-text">
                {{ $t("User.mate.basicInfo.gender") }}
              </span>
            </div>
            <select
              v-model="gender"
              class="select select-bordered select-sm sm:select-md"
            >
              <option
                v-for="(item, idx) in GenderOptions"
                :key="idx"
                :disabled="item.value === gender"
                :value="item.value"
              >
                {{ item.text }}
              </option>
            </select>
          </label>
        </div>
        <div v-else>
          <div class="flex gap-2 items-center border p-2 rounded">
            <UserAvatar
              :avatar-url="mateStore.currentMate!.mediaUrl"
              class="h-7"
            />
            <div class="flex flex-col">
              <div class="text-base">
                {{ mateStore.currentMate!.name }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-1 h-full">
        <div
          v-if="!user.isHideNSFW && !targetMate?.isAllowedInjected"
          class="badge badge-neutral"
        >
          {{ $t("Message.notAllowInjection") }}
        </div>
        <van-tabs
          v-model:active="chatPrepareTab"
          class="chat-prepare-tab"
          shrink
        >
          <van-tab :title="$t('Chats.chatScene.name')">
            <div class="py-4 flex flex-col gap-2">
              <div v-if="false" class="join">
                <button
                  :class="{
                    'btn-primary': sceneTab === 'specific',
                  }"
                  class="join-item btn btn-xs"
                  @click="sceneTab = 'specific'"
                >
                  {{ $t("Chats.chatScene.specific") }}
                  <template v-if="!isSceneLoading">
                    ({{ useShortCount(sceneList.length + 1) }})
                  </template>
                </button>
                <button
                  :class="{
                    'btn-primary': sceneTab === 'generic',
                  }"
                  :disabled="
                    isSceneLoading ||
                    !chatSceneStore.genericChatSceneList.length
                  "
                  class="join-item btn btn-xs"
                  @click="sceneTab = 'generic'"
                >
                  {{ $t("Chats.chatScene.generic") }}
                  <template v-if="!isSceneLoading">
                    ({{
                      useShortCount(chatSceneStore.genericChatSceneList.length)
                    }})
                  </template>
                </button>
              </div>
              <div class="flex flex-col gap-1 flex-grow">
                <div class="flex flex-col gap-1">
                  <template v-if="sceneTab === 'specific'">
                    <div
                      v-if="isSceneLoading"
                      class="flex gap-2 border p-2 h-28 rounded"
                    >
                      <div
                        class="skeleton-loading-bg h-24 shrink-0 aspect-[3/4] rounded"
                      />
                      <div
                        class="overflow-hidden flex-grow text-xs flex flex-col gap-1"
                      >
                        <div class="skeleton-loading-bg h-4 w-full rounded" />
                        <div class="skeleton-loading-bg h-4 w-full rounded" />
                        <div class="skeleton-loading-bg h-4 w-[60%] rounded" />
                      </div>
                    </div>

                    <template v-for="item in sceneList" v-else :key="item.id">
                      <SceneCard
                        :chat-scene="item"
                        :selected="selectedSceneId == item.id!"
                        :target-mate="targetMate!"
                        @click="selectedSceneId = item.id"
                      />
                    </template>
                  </template>
                  <template v-else>
                    <div
                      v-if="isSceneLoading"
                      class="flex gap-2 border p-2 h-28 rounded"
                    >
                      <div
                        class="skeleton-loading-bg h-24 shrink-0 aspect-[3/4] rounded"
                      />
                      <div
                        class="overflow-hidden flex-grow text-xs flex flex-col gap-1"
                      >
                        <div class="skeleton-loading-bg h-4 w-full rounded" />
                        <div class="skeleton-loading-bg h-4 w-full rounded" />
                        <div class="skeleton-loading-bg h-4 w-[60%] rounded" />
                      </div>
                    </div>
                    <template v-else>
                      <SceneCard
                        v-for="item in chatSceneStore.genericChatSceneList"
                        :key="item.id"
                        :chat-scene="item"
                        :selected="selectedSceneId == item.id!"
                        :target-mate="targetMate!"
                        @click="selectedSceneId = item.id"
                      />
                    </template>
                  </template>
                </div>
              </div>
            </div>
          </van-tab>
          <van-tab
            v-if="
              !user.isHideNSFW &&
              targetMate?.isAllowedInjected &&
              personaStore.genericMatePersonaList.length > 0
            "
            :title="$t('User.mate.module.persona')"
          >
            <div class="py-4 flex flex-col gap-2">
              <div class="flex flex-col gap-1 flex-grow">
                <div
                  :class="{
                    '': selectedPersonaId,
                    'border-primary': !selectedPersonaId,
                  }"
                  class="rounded p-2 border cursor-pointer flex items-center gap-2"
                  @click="selectedPersonaId = undefined"
                >
                  <i
                    v-if="!selectedPersonaId"
                    class="iconify mingcute--thought-fill text-primary"
                  />
                  <i v-else class="iconify mingcute--thought-line" />
                  {{ $t("Chats.matePersona.default") }}
                </div>
                <div
                  v-if="isPersonaLoading"
                  class="flex gap-2 border p-2 h-28 rounded"
                >
                  <div
                    class="skeleton-loading-bg h-24 shrink-0 aspect-[3/4] rounded"
                  />
                  <div
                    class="overflow-hidden flex-grow text-xs flex flex-col gap-1"
                  >
                    <div class="skeleton-loading-bg h-4 w-full rounded" />
                    <div class="skeleton-loading-bg h-4 w-full rounded" />
                    <div class="skeleton-loading-bg h-4 w-[60%] rounded" />
                  </div>
                </div>
                <template v-else>
                  <PersonaCard
                    v-for="item in personaStore.genericMatePersonaList"
                    :key="item.id"
                    :matePersona="item"
                    :selected="selectedPersonaId == item.id!"
                    :target-mate="targetMate"
                    @click="selectedPersonaId = item.id"
                  />
                </template>
              </div>
            </div>
          </van-tab>
        </van-tabs>
      </div>
    </div>
  </Popup>
</template>
<style>
.chat-prepare-tab .van-tabs__nav {
  @apply pl-0;
}

.chat-prepare-tab .van-tab__text {
  @apply text-base;
}
</style>
